import React, { useState } from 'react';
import {
  Box,
  Typography,
  Input,
  Button,
  Radio,
  RadioGroup,
  Select,
  Option,
  Modal,
  List,
  ListItem,
  ListItemButton,
  Checkbox
} from '@mui/joy';

const EvaluationForm = ({ 
  evaluation, 
  onEvaluationChange, 
  services,
  readOnly = false, 
  showAdvancedView = false,
  onToggleAdvancedView = () => {},
  onSave,
  submitButtonText = "Speichern",
  onDelete
}) => {
  // State for services dialog
  const [isServicesDialogOpen, setIsServicesDialogOpen] = useState(false);
  const [selectedField, setSelectedField] = useState('baseServices');

  // Define a common style for all radio groups
  const radioGroupStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: 2,
    alignItems: 'center',
    height: '40px', // Force consistent height
    '& .MuiRadio-root': {
      padding: '0 8px' // Reduce padding to ensure alignment
    }
  };

  // Handler for input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    onEvaluationChange({
      ...evaluation,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  // Helper function to ensure radio buttons always have a value
  const handleOperatorChange = (e, newValue) => {
    const name = e.target.name;
    onEvaluationChange({
      ...evaluation,
      [name]: parseInt(newValue)
    });
  };

  // Handle age operator changes coming from the dropdown
  const handleAgeOperatorChange = (e, newValue) => {
    onEvaluationChange({
      ...evaluation,
      ageOperator: parseInt(newValue)
    });
  };

  // Function to handle service selection
  const handleServiceSelect = (service) => {
    // Check if service code is already in the field
    const currentCodes = evaluation[selectedField].split(',').map(code => code.trim()).filter(Boolean);
    
    if (!currentCodes.includes(service.code)) {
      // Add the service code if it's not already there
      const newValue = currentCodes.length > 0 
        ? `${evaluation[selectedField]}, ${service.code}`
        : service.code;
      
      onEvaluationChange({
        ...evaluation,
        [selectedField]: newValue
      });
    }
    
    // Close the dialog after selection
    setIsServicesDialogOpen(false);
  };

  // Function to open service selection dialog for a specific field
  const openServicesDialog = (fieldName) => {
    setSelectedField(fieldName);
    setIsServicesDialogOpen(true);
  };

  const handleDelete = () => {
    if (window.confirm('Möchten Sie diese Auswertung wirklich löschen?')) {
      onDelete(evaluation.id);
    }
  };

  const handleSave = () => {
    if (!evaluation.evaluationName || evaluation.evaluationName.trim() === '') {
      alert('Bitte geben Sie einen Namen für die benutzerdefinierte Auswertung ein.');
      return;
    }
  
    const serializedContent = serializeEvaluation(evaluation);
    onSave(evaluation.evaluationName, serializedContent);
  };

  return (
    <>
      {/* Basic form fields */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography>Name der Auswertung:</Typography>
          <Input
            name="evaluationName"
            placeholder="Name der Auswertung"
            value={evaluation.evaluationName}
            onChange={handleInputChange}
            disabled={readOnly}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="solid" 
            color="primary" 
            onClick={onToggleAdvancedView}
          >
            {showAdvancedView ? "Erweiterte Ansicht ausblenden" : "Erweiterte Ansicht"}
          </Button>
        </Box>

        {showAdvancedView && (
          <>
            <Typography level="h3">Konsultationszeitraum:</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography>Vom</Typography>
              <Input
                type="date"
                name="dateSpanStart"
                value={evaluation.dateSpanStart}
                onChange={handleInputChange}
                disabled={readOnly}
              />
              <Typography>Bis</Typography>
              <Input
                type="date"
                name="dateSpanEnd"
                value={evaluation.dateSpanEnd}
                onChange={handleInputChange}
                disabled={readOnly}
              />
            </Box>

            <Typography level="h3">Alter:</Typography>
            <Select
              name="ageOperator"
              value={evaluation.ageOperator.toString()}
              onChange={handleAgeOperatorChange}
              disabled={readOnly}
              sx={{ mb: 1 }}
            >
              <Option value="0">Jünger als</Option>
              <Option value="1">Genau</Option>
              <Option value="2">Älter als</Option>
              <Option value="3">Zwischen</Option>
            </Select>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography>{evaluation.ageOperator === 3 ? "Von" : "Alter"}</Typography>
              <Input
                type="number"
                name="ageFrom"
                value={evaluation.ageFrom}
                onChange={handleInputChange}
                disabled={readOnly}
                sx={{ width: '80px' }}
              />
              {evaluation.ageOperator === 3 && (
                <>
                  <Typography>Bis</Typography>
                  <Input
                    type="number"
                    name="ageTo"
                    value={evaluation.ageTo}
                    onChange={handleInputChange}
                    disabled={readOnly}
                    sx={{ width: '80px' }}
                  />
                </>
              )}
              <Typography>Jahre</Typography>
            </Box>

            <Typography>Mit Basis-Gebührenordnungsposition(en) im Zeitraum</Typography>
            <Input
              type="checkbox"
              name="baseServicesWithinDateSpan"
              checked={evaluation.baseServicesWithinDateSpan}
              onChange={handleInputChange}
              disabled={readOnly}
            />

            <Typography>Mit Gebührenordnungsposition(en) im Zeitraum</Typography>
            <Input
              type="checkbox"
              name="servicesWithinDateSpan"
              checked={evaluation.servicesWithinDateSpan}
              onChange={handleInputChange}
              disabled={readOnly}
            />

            <Typography>Ohne Gebührenordnungsposition(en) im Zeitraum</Typography>
            <Input
              type="checkbox"
              name="notServicesWithinDateSpan"
              checked={evaluation.notServicesWithinDateSpan}
              onChange={handleInputChange}
              disabled={readOnly}
            />

            <Typography>Mit Diagnose(n) im Zeitraum</Typography>
            <Input
              type="checkbox"
              name="diagnosesWithinDateSpan"
              checked={evaluation.diagnosesWithinDateSpan}
              onChange={handleInputChange}
              disabled={readOnly}
            />

            <Typography>Ohne Diagnose(n) im Zeitraum</Typography>
            <Input
              type="checkbox"
              name="notDiagnosesWithinDateSpan"
              checked={evaluation.notDiagnosesWithinDateSpan}
              onChange={handleInputChange}
              disabled={readOnly}
            />

            <Typography level="h3">Mit Institutionskennzeichen:</Typography>
            <RadioGroup
              name="institutionIdsOperator"
              value={evaluation.institutionIdsOperator.toString()}
              onChange={(e) => handleOperatorChange(e, e.target.value)}
              disabled={readOnly}
              orientation="horizontal"
              sx={radioGroupStyle}
            >
              <Radio value="0" label="UND" />
              <Radio value="1" label="ODER" />
            </RadioGroup>
            <Input
              name="institutionIds"
              placeholder="Institutionskennzeichen"
              value={evaluation.institutionIds}
              onChange={handleInputChange}
              disabled={readOnly}
            />

            <Typography level="h3">Quartals-übergreifende Auswertung:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                name="excludeIfFoundAcrossQuarters"
                checked={Boolean(evaluation.excludeIfFoundAcrossQuarters)}
                onChange={(e) => {
                  onEvaluationChange({
                    ...evaluation,
                    excludeIfFoundAcrossQuarters: e.target.checked
                  });
                }}
                disabled={readOnly}
              />
              <Typography level="body2">
                Patienten ausschließen, die in anderen Quartalen gefunden wurden (für Impfstatus-Abfragen)
              </Typography>
            </Box>
          </>
        )}

        <Typography level="h3">Mit Basis-Gebührenordnungsposition(en):</Typography>
        <RadioGroup
          name="baseServicesOperator"
          value={evaluation.baseServicesOperator.toString()}
          onChange={(e) => handleOperatorChange(e, e.target.value)}
          disabled={readOnly}
          orientation="horizontal"
          sx={radioGroupStyle}
        >
          <Radio value="0" label="UND" />
          <Radio value="1" label="ODER" />
        </RadioGroup>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Input
            name="baseServices"
            placeholder="Basis-Gebührenordnungsposition(en)"
            value={evaluation.baseServices}
            onChange={handleInputChange}
            disabled={readOnly}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={() => openServicesDialog('baseServices')}
            disabled={readOnly}
            sx={{ minWidth: 'auto' }}
          >
            Auswählen
          </Button>
        </Box>

        <Typography level="h3">Mit Gebührenordnungsposition(en):</Typography>
        <RadioGroup
          name="servicesOperator"
          value={evaluation.servicesOperator.toString()}
          onChange={(e) => handleOperatorChange(e, e.target.value)}
          disabled={readOnly}
          orientation="horizontal"
          sx={radioGroupStyle}
        >
          <Radio value="0" label="UND" />
          <Radio value="1" label="ODER" />
        </RadioGroup>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Input
            name="services"
            placeholder="Gebührenordnungsposition(en)"
            value={evaluation.services}
            onChange={handleInputChange}
            disabled={readOnly}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={() => openServicesDialog('services')}
            disabled={readOnly}
            sx={{ minWidth: 'auto' }}
          >
            Auswählen
          </Button>
        </Box>

        <Typography level="h3">Ohne Gebührenordnungsposition(en):</Typography>
        <RadioGroup
          name="notServicesOperator"
          value={evaluation.notServicesOperator.toString()}
          onChange={(e) => handleOperatorChange(e, e.target.value)}
          disabled={readOnly}
          orientation="horizontal"
          sx={radioGroupStyle}
        >
          <Radio value="2" label="Nicht-UND" />
          <Radio value="3" label="Nicht-ODER" />
        </RadioGroup>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Input
            name="notServices"
            placeholder="Ohne Gebührenordnungsposition(en)"
            value={evaluation.notServices}
            onChange={handleInputChange}
            disabled={readOnly}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={() => openServicesDialog('notServices')}
            disabled={readOnly}
            sx={{ minWidth: 'auto' }}
          >
            Auswählen
          </Button>
        </Box>

        <Typography level="h3">Mit Diagnose(n):</Typography>
        <RadioGroup
          name="diagnosesOperator"
          value={evaluation.diagnosesOperator.toString()}
          onChange={(e) => handleOperatorChange(e, e.target.value)}
          disabled={readOnly}
          orientation="horizontal"
          sx={radioGroupStyle}
        >
          <Radio value="0" label="UND" />
          <Radio value="1" label="ODER" />
        </RadioGroup>
        <Input
          name="diagnoses"
          placeholder="Diagnosen"
          value={evaluation.diagnoses}
          onChange={handleInputChange}
          disabled={readOnly}
        />

        <Typography level="h3">Ohne Diagnose(n):</Typography>
        <RadioGroup
          name="notDiagnosesOperator"
          value={evaluation.notDiagnosesOperator.toString()}
          onChange={(e) => handleOperatorChange(e, e.target.value)}
          disabled={readOnly}
          orientation="horizontal"
          sx={radioGroupStyle}
        >
          <Radio value="2" label="Nicht-UND" />
          <Radio value="3" label="Nicht-ODER" />
        </RadioGroup>
        <Input
          name="notDiagnoses"
          placeholder="Ohne Diagnosen"
          value={evaluation.notDiagnoses}
          onChange={handleInputChange}
          disabled={readOnly}
        />

        {/* Save Button */}
        {onSave && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
            <Button 
              variant="solid" 
              color="primary" 
              onClick={handleSave}
              disabled={readOnly}
            >
              {submitButtonText}
            </Button>
          </Box>
        )}

        {/* Delete Button */}
        {onDelete && (
          <Button 
            variant="outlined" 
            color="danger" 
            onClick={handleDelete}
            sx={{ mt: 2 }}
          >
            Löschen
          </Button>
        )}
      </Box>

      {/* Services selection modal */}
      <Modal
        open={isServicesDialogOpen}
        onClose={() => setIsServicesDialogOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '8px',
            p: 3,
            width: '80%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto',
          }}
        >
          <Typography level="h4" sx={{ mb: 2 }}>Gebührenordnungspositionen auswählen</Typography>
          <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {services.map((service, index) => (
              <ListItem key={index}>
                <ListItemButton 
                  onClick={() => handleServiceSelect(service)}
                  sx={{
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography fontWeight="bold">{service.code}</Typography>
                  <Typography>{service.description}</Typography>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Button 
            onClick={() => setIsServicesDialogOpen(false)}
            sx={{ mt: 2 }}
            variant="outlined"
            color="neutral"
          >
            Schließen
          </Button>
        </Box>
      </Modal>
    </>
  );
};

// Helper functions for serialization/deserialization
export const serializeEvaluation = (evaluation) => {
  return [
    evaluation.evaluationName.trim(),
    evaluation.dateSpanStart,
    evaluation.dateSpanEnd,
    evaluation.ageFrom,
    evaluation.ageTo,
    evaluation.ageOperator,
    evaluation.institutionIds.trim(),
    evaluation.institutionIdsOperator,
    evaluation.baseServices.trim(),
    evaluation.baseServicesOperator,
    evaluation.baseServicesWithinDateSpan ? "1" : "0",
    evaluation.services.trim(),
    evaluation.servicesOperator,
    evaluation.servicesWithinDateSpan ? "1" : "0",
    evaluation.notServices.trim(),
    evaluation.notServicesOperator,
    evaluation.notServicesWithinDateSpan ? "1" : "0",
    evaluation.diagnoses.trim(),
    evaluation.diagnosesOperator,
    evaluation.diagnosesWithinDateSpan ? "1" : "0",
    evaluation.notDiagnoses.trim(),
    evaluation.notDiagnosesOperator,
    evaluation.notDiagnosesWithinDateSpan ? "1" : "0",
    (evaluation.excludeIfFoundAcrossQuarters === true) ? "1" : "0",
  ].join('|');
};

export const deserializeEvaluation = (serializedEvaluation) => {
  const objectMember = serializedEvaluation.split('|');

  if (objectMember.length < 23) {
    console.error('Die Auswertungsdatei enthält nicht alle erforderlichen Felder.');
    return null;
  }

  try {
    const result = {
      evaluationName: objectMember[0],
      dateSpanStart: objectMember[1],
      dateSpanEnd: objectMember[2],
      ageFrom: parseInt(objectMember[3]),
      ageTo: parseInt(objectMember[4]),
      ageOperator: parseInt(objectMember[5]),
      institutionIds: objectMember[6],
      institutionIdsOperator: parseInt(objectMember[7]),
      baseServices: objectMember[8],
      baseServicesOperator: parseInt(objectMember[9]),
      baseServicesWithinDateSpan: objectMember[10] === '1',
      services: objectMember[11],
      servicesOperator: parseInt(objectMember[12]),
      servicesWithinDateSpan: objectMember[13] === '1',
      notServices: objectMember[14],
      notServicesOperator: parseInt(objectMember[15]),
      notServicesWithinDateSpan: objectMember[16] === '1',
      diagnoses: objectMember[17],
      diagnosesOperator: parseInt(objectMember[18]),
      diagnosesWithinDateSpan: objectMember[19] === '1',
      notDiagnoses: objectMember[20],
      notDiagnosesOperator: parseInt(objectMember[21]),
      notDiagnosesWithinDateSpan: objectMember[22] === '1',
      // Handle the new field - if it doesn't exist (old format), default to false
      excludeIfFoundAcrossQuarters: objectMember.length > 23 ? objectMember[23] === '1' : false,
    };
    
    console.log('Deserialized evaluation:', result);
    console.log('excludeIfFoundAcrossQuarters value:', result.excludeIfFoundAcrossQuarters);
    
    return result;
  } catch (error) {
    console.error('Einzelne Parameter der Auswertungsdatei können nicht verarbeitet werden.', error);
    return null;
  }
};

// Default empty evaluation
export const createEmptyEvaluation = () => ({
  evaluationName: '',
  dateSpanStart: '',
  dateSpanEnd: '',
  ageFrom: 0,
  ageTo: 0,
  ageOperator: 0, // OPERATOR_LESSTHAN
  institutionIds: '',
  institutionIdsOperator: 0, // 0 = UND by default
  baseServices: '',
  baseServicesOperator: 0, // 0 = UND by default
  baseServicesWithinDateSpan: false,
  services: '',
  servicesOperator: 0, // 0 = UND by default
  servicesWithinDateSpan: false,
  notServices: '',
  notServicesOperator: 2, // 2 = Nicht-UND by default
  notServicesWithinDateSpan: false,
  diagnoses: '',
  diagnosesOperator: 0, // 0 = UND by default
  diagnosesWithinDateSpan: false,
  notDiagnoses: '',
  notDiagnosesOperator: 2, // 2 = Nicht-UND by default
  notDiagnosesWithinDateSpan: false,
  excludeIfFoundAcrossQuarters: false,
});

export default EvaluationForm;
